/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * ⚠️ DEPRECATED: Legacy Interactive Story UI — DO NOT EDIT ⚠️
 * 
 * This screen is part of the deprecated interactive story journey.
 * It is NOT routed in production and kept only for backwards compatibility.
 * 
 * The LIVE production UI is the Hub System Assessment at /hub
 * 
 * DO NOT make changes to this file unless fixing security vulnerabilities.
 * All new features should be added to the Hub UI instead.
 */

/**
 * Screen 1: Your Home
 * Confirms the recommendation is based on the user's home, usage, and priorities
 * Now includes recommendation summary upfront (Part 1)
 */

import { useRecommendation } from '../../context/RecommendationContext';
import { getOptionDisplayTitle } from '../../utils/displayNames';
import { buildSummaryLine, formatReportValue } from './reportUtils';

function Screen1YourHome() {
  const { recommendationResult, collectedData } = useRecommendation();
  const primaryOption = recommendationResult?.output?.primary;

  const summaryText = primaryOption
    ? `Based on the details we captured, ${getOptionDisplayTitle(primaryOption)} is the best overall fit for your home.`
    : 'Based on the details we captured, a modern system boiler is the best overall fit, balancing comfort, hot water performance, and long-term reliability.';

  const homeSummary = [
    {
      label: 'Property',
      value: buildSummaryLine(collectedData?.propertyType, collectedData?.propertyAgeBand),
    },
    {
      label: 'Bedrooms & bathrooms',
      value: buildSummaryLine(
        collectedData?.bedrooms ? `${formatReportValue(collectedData.bedrooms)} bedrooms` : null,
        collectedData?.bathrooms ? `${formatReportValue(collectedData.bathrooms)} bathrooms` : null,
      ),
    },
    {
      label: 'Household size',
      value: buildSummaryLine(collectedData?.occupancyCount, collectedData?.occupancyTiming),
    },
    {
      label: 'Hot water needs',
      value: buildSummaryLine(collectedData?.hotWaterDemand, collectedData?.usagePattern),
    },
    {
      label: 'Plans',
      value: buildSummaryLine(collectedData?.lengthOfStay, collectedData?.urgency),
    },
  ];

  // Add priorities if available
  if (collectedData?.upgradePriorities && collectedData.upgradePriorities.length > 0) {
    homeSummary.push({
      label: 'Your priorities',
      value: formatReportValue(collectedData.upgradePriorities),
    });
  }

  return (
    <div className="interactive-story-screen">
      <h1 className="screen-title">Your Home</h1>

      <div className="screen-body">
        {/* Part 1: Recommendation Summary Block */}
        <div className="recommendation-summary-block">
          <h2 className="summary-heading">Our recommendation for your home</h2>
          <p className="summary-text">
            {summaryText}
          </p>
          <p className="summary-disclaimer">
            Based on the information provided and typical assumptions where details were unknown.
          </p>
        </div>

        <div className="report-highlights">
          <h2 className="report-highlights-title">Your report at a glance</h2>
          <div className="report-highlights-grid">
            {homeSummary.map((item) => (
              <div key={item.label} className="report-highlight-card">
                <p className="report-highlight-label">{item.label}</p>
                <p className="report-highlight-value">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="screen-text">
          This recommendation is based on your home, your usage patterns, and your priorities.
        </p>
        <p className="screen-text secondary">
          We&apos;ve looked at what you have now, what you need, and what will work for your situation.
        </p>
      </div>
    </div>
  );
}

export default Screen1YourHome;
